import React, { useState, useEffect, useMemo } from 'react';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { PickersLayoutRoot, PickersLayoutContentWrapper, usePickerLayout } from '@mui/x-date-pickers/PickersLayout';
import dayjs from 'dayjs';
import customParseFormat from 'dayjs/plugin/customParseFormat';
import dateSelectorIcon from '../assets/protocols/date-selector.svg';
import './DatePicker.css';

// Extend dayjs with the plugin to support custom format parsing
dayjs.extend(customParseFormat);

// Custom layout that puts action bar at bottom
const CustomLayout = (props) => {
  const { content, actionBar } = usePickerLayout(props);
  
  return (
    <PickersLayoutRoot 
      ownerState={props} 
      sx={{ 
        display: 'flex !important', 
        flexDirection: 'column !important',
        '& .MuiPickersLayout-actionBar': {
          order: '999 !important',
        }
      }}
    >
      <PickersLayoutContentWrapper className="MuiPickersLayout-contentWrapper">
        {content}
      </PickersLayoutContentWrapper>
      <div className="MuiPickersLayout-actionBar">
        {actionBar}
      </div>
    </PickersLayoutRoot>
  );
};

// Custom action bar with Today and Clear buttons
const CustomActionBar = ({ onClear, onSetToday }) => {
  return (
    <div style={{
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'center',
      padding: '12px 16px',
      borderTop: '1px solid #E4E7EC',
      backgroundColor: '#fafbfc',
      gap: '10px',
      width: '100%',
      boxSizing: 'border-box'
    }}>
      <button
        onClick={onSetToday}
        style={{
          padding: '10px 20px',
          border: 'none',
          borderRadius: '8px',
          backgroundColor: '#103c6e',
          color: 'white',
          fontSize: '14px',
          fontWeight: '600',
          cursor: 'pointer',
          transition: 'all 200ms ease',
          boxShadow: '0 2px 8px rgba(16, 60, 110, 0.2)',
          flex: 1
        }}
        onMouseEnter={(e) => {
          e.target.style.backgroundColor = '#0d3157';
          e.target.style.transform = 'translateY(-1px)';
          e.target.style.boxShadow = '0 4px 12px rgba(16, 60, 110, 0.3)';
        }}
        onMouseLeave={(e) => {
          e.target.style.backgroundColor = '#103c6e';
          e.target.style.transform = 'translateY(0)';
          e.target.style.boxShadow = '0 2px 8px rgba(16, 60, 110, 0.2)';
        }}
      >
        היום
      </button>
      <button
        onClick={onClear}
        style={{
          padding: '10px 20px',
          border: '1.5px solid #D0D5DD',
          borderRadius: '8px',
          backgroundColor: 'white',
          color: '#667085',
          fontSize: '14px',
          fontWeight: '600',
          cursor: 'pointer',
          transition: 'all 200ms ease',
          flex: 1
        }}
        onMouseEnter={(e) => {
          e.target.style.backgroundColor = '#f9fafb';
          e.target.style.borderColor = '#103c6e';
          e.target.style.color = '#103c6e';
          e.target.style.transform = 'translateY(-1px)';
        }}
        onMouseLeave={(e) => {
          e.target.style.backgroundColor = 'white';
          e.target.style.borderColor = '#D0D5DD';
          e.target.style.color = '#667085';
          e.target.style.transform = 'translateY(0)';
        }}
      >
        אפס
      </button>
    </div>
  );
};

const CustomDatePicker = ({ value, onChange, error, helperText, placeholder, label }) => {
  // Parse the incoming value (string DD/MM/YYYY) to a dayjs object
  const parsedValue = useMemo(() => {
    if (!value) return null;
    const d = dayjs(value, 'DD/MM/YYYY');
    return d.isValid() ? d : null;
  }, [value]);

  const [internalDate, setInternalDate] = useState(parsedValue);
  const [tempDate, setTempDate] = useState(parsedValue);
  const [open, setOpen] = useState(false);

  // Sync internal state with props when props change
  useEffect(() => {
    if (parsedValue === null && internalDate === null) return;
    if (parsedValue && internalDate && parsedValue.isSame(internalDate)) return;

    setInternalDate(parsedValue);
    setTempDate(parsedValue);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [parsedValue]);

  const handleOpen = () => {
    setTempDate(internalDate);
    setOpen(true);
  };

  const handleClose = () => {
    setOpen(false);
  };

  const handleClear = () => {
    setTempDate(null);
    setInternalDate(null);
    onChange('');
    setOpen(false);
  };

  const handleSetToday = () => {
    const today = dayjs();
    setTempDate(today);
    setInternalDate(today);
    onChange(today.format('DD/MM/YYYY'));
    setOpen(false);
  };

  const handleChange = (newValue) => {
    setTempDate(newValue);
    setInternalDate(newValue);
    if (newValue === null) {
      onChange('');
    } else if (newValue.isValid()) {
      onChange(newValue.format('DD/MM/YYYY'));
    }
    setOpen(false);
  };

  // For direct input changes (typing)
  const handleInputChange = (newValue) => {
    setInternalDate(newValue);
    setTempDate(newValue);
    if (newValue === null) {
      onChange('');
    } else if (newValue.isValid()) {
      onChange(newValue.format('DD/MM/YYYY'));
    }
  };

  // Unified calendar styling for both desktop and mobile
  const commonCalendarStyles = {
    '& .MuiPickersLayout-actionBar': {
      padding: 0,
      margin: 0,
    },
    '& .MuiDialogActions-root': {
      padding: 0,
    },
    '& .MuiPickersCalendarHeader-root': {
      paddingLeft: '16px',
      paddingRight: '16px',
      marginTop: '8px',
      marginBottom: '4px',
    },
    '& .MuiPickersCalendarHeader-label': {
      fontSize: '16px',
      fontWeight: '700',
      color: '#103c6e',
      cursor: 'pointer',
      transition: 'color 200ms ease',
      '&:hover': {
        color: '#0d3157',
      },
    },
    '& .MuiPickersArrowSwitcher-button': {
      color: '#103c6e',
      padding: '8px',
      borderRadius: '8px',
      transition: 'all 200ms ease',
      '&:hover': {
        backgroundColor: '#e8f0f8',
        color: '#0d3157',
        transform: 'scale(1.1)',
      },
    },
    '& .MuiDayCalendar-weekDayLabel': {
      fontSize: '13px',
      fontWeight: '600',
      color: '#667085',
      width: '38px',
      height: '38px',
      margin: '2px',
    },
    '& .MuiPickersDay-root': {
      fontSize: '14px',
      width: '38px',
      height: '38px',
      borderRadius: '10px',
      margin: '2px',
      color: '#344054',
      fontWeight: '500',
      transition: 'all 200ms cubic-bezier(0.4, 0, 0.2, 1)',
      '&:hover': {
        backgroundColor: '#e8f0f8',
        color: '#103c6e',
        transform: 'scale(1.05)',
        fontWeight: '600',
      },
      '&.Mui-selected': {
        backgroundColor: '#103c6e',
        color: 'white',
        fontWeight: '700',
        boxShadow: '0 4px 12px rgba(16, 60, 110, 0.3)',
        '&:hover': {
          backgroundColor: '#0d3157',
          transform: 'scale(1.05)',
        },
      },
      '&.MuiPickersDay-today': {
        border: '2px solid #103c6e',
        fontWeight: '700',
        '&:not(.Mui-selected)': {
          backgroundColor: 'transparent',
          color: '#103c6e',
          '&:hover': {
            backgroundColor: '#e8f0f8',
            transform: 'scale(1.05)',
          },
        },
        '&.Mui-selected': {
          backgroundColor: '#103c6e',
          color: 'white',
          border: '2px solid #103c6e',
          boxShadow: '0 4px 12px rgba(16, 60, 110, 0.3)',
        },
      },
    },
    '& .MuiPickersYear-yearButton': {
      fontSize: '14px',
      borderRadius: '10px',
      color: '#344054',
      fontWeight: '500',
      padding: '10px',
      margin: '4px',
      transition: 'all 200ms cubic-bezier(0.4, 0, 0.2, 1)',
      '&:hover': {
        backgroundColor: '#e8f0f8',
        color: '#103c6e',
        fontWeight: '600',
        transform: 'scale(1.05)',
      },
      '&.Mui-selected': {
        backgroundColor: '#103c6e',
        color: 'white',
        fontWeight: '700',
        boxShadow: '0 4px 12px rgba(16, 60, 110, 0.3)',
        '&:hover': {
          backgroundColor: '#0d3157',
          transform: 'scale(1.05)',
        },
      },
    },
    '& .MuiPickersMonth-monthButton': {
      fontSize: '14px',
      borderRadius: '10px',
      color: '#344054',
      fontWeight: '500',
      padding: '10px',
      margin: '4px',
      transition: 'all 200ms cubic-bezier(0.4, 0, 0.2, 1)',
      '&:hover': {
        backgroundColor: '#e8f0f8',
        color: '#103c6e',
        fontWeight: '600',
        transform: 'scale(1.05)',
      },
      '&.Mui-selected': {
        backgroundColor: '#103c6e',
        color: 'white',
        fontWeight: '700',
        boxShadow: '0 4px 12px rgba(16, 60, 110, 0.3)',
        '&:hover': {
          backgroundColor: '#0d3157',
          transform: 'scale(1.05)',
        },
      },
    },
  };

  // Desktop popper styling
  const popperSx = {
    '& .MuiPaper-root': {
      borderRadius: '14px',
      boxShadow: '0 12px 48px rgba(0, 0, 0, 0.18)',
      border: '1px solid #E4E7EC',
      overflow: 'hidden',
      padding: '6px',
    },
    ...commonCalendarStyles,
  };

  return (
    <LocalizationProvider dateAdapter={AdapterDayjs}>
      <div className="custom-date-picker-wrapper">
        <DatePicker
          label={label}
          value={open ? tempDate : internalDate}
          onChange={open ? handleChange : handleInputChange}
          open={open}
          onOpen={handleOpen}
          onClose={handleClose}
          format="DD/MM/YYYY"
          slots={{
            openPickerIcon: () => <img src={dateSelectorIcon} alt="calendar" style={{ width: 24, height: 24 }} />,
            layout: CustomLayout,
            actionBar: () => (
              <CustomActionBar
                onClear={handleClear}
                onSetToday={handleSetToday}
              />
            ),
          }}
          slotProps={{
            textField: {
              placeholder: placeholder || 'DD/MM/YYYY',
              error: !!error,
              helperText: helperText,
              fullWidth: true,
              variant: 'outlined',
              onClick: handleOpen,
              onContextMenu: (e) => e.preventDefault(),
              inputProps: { 
                readOnly: true,
                style: { userSelect: 'none', WebkitUserSelect: 'none' }
              },
              sx: {
                '& .MuiOutlinedInput-root': {
                  borderRadius: '10px',
                  backgroundColor: 'transparent',
                  cursor: 'pointer',
                  userSelect: 'none',
                  transition: 'all 200ms ease',
                  '& fieldset': {
                    borderColor: '#E4E7EC',
                    borderWidth: '1.5px',
                    transition: 'all 200ms cubic-bezier(0.4, 0, 0.2, 1)',
                  },
                  '&:hover fieldset': {
                    borderColor: '#103c6e',
                    boxShadow: '0 0 0 3px rgba(16, 60, 110, 0.08)',
                  },
                  '&.Mui-focused fieldset': {
                    borderColor: '#103c6e',
                    borderWidth: '2px',
                    boxShadow: '0 0 0 3px rgba(16, 60, 110, 0.12)',
                  },
                  '& input': {
                    padding: '16.5px 14px',
                    fontSize: '16px',
                    fontFamily: 'inherit',
                    fontWeight: '500',
                    color: 'inherit',
                    cursor: 'pointer',
                    userSelect: 'none',
                    WebkitUserSelect: 'none',
                    caretColor: 'transparent'
                  }
                },
                '& .MuiFormHelperText-root': {
                  marginLeft: 0,
                  fontSize: '13px',
                  marginTop: '6px'
                }
              }
            },
            inputAdornment: {
              position: 'end',
              sx: {
                marginRight: '8px'
              }
            },
            mobilePaper: {
              sx: {
                borderRadius: '14px',
                overflow: 'hidden',
                padding: '6px',
                '& .MuiPickersToolbar-root': {
                  display: 'none',
                },
                ...commonCalendarStyles,
              }
            },
            dialog: {
              sx: {
                '& .MuiDialog-paper': {
                  borderRadius: '14px',
                  overflow: 'hidden',
                  margin: '16px',
                  boxShadow: '0 12px 48px rgba(0, 0, 0, 0.18)',
                  padding: '6px',
                },
                '& .MuiPickersToolbar-root': {
                  display: 'none',
                },
                ...commonCalendarStyles,
              }
            },
            desktopPaper: {
              sx: {
                boxShadow: '0 12px 48px rgba(0, 0, 0, 0.18)',
                borderRadius: '14px',
              }
            },
            popper: {
              sx: popperSx,
              disablePortal: false,
              keepMounted: false,
            },
            modal: {
              keepMounted: false,
              disableScrollLock: true,
            }
          }}
        />
      </div>
    </LocalizationProvider>
  );
};

export default CustomDatePicker;